/**
 * <license header>
 */

import { ShippingCarrierMethodData } from './method/types';
import { ShippingCarrierData } from './types';
import ShippingCarrierMethod from './method';

/**
 * Builder class for constructing shipping carriers with methods and metadata
 */
class ShippingCarrier {
  private carrierData: Partial<ShippingCarrierData>;
  private addedMethods: ShippingCarrierMethodData[] = [];
  private removedMethods: string[] = [];

  constructor(code: string, callback?: (builder: ShippingCarrier) => void) {
    this.validateCarrierCode(code);

    this.carrierData = {
      code,
      active: true,
      tracking_available: true,
      shipping_labels_available: true,
    };
    this.addedMethods = [];
    this.removedMethods = [];

    if (callback) {
      callback(this);
    }
  }

  /**
   * Validates that the carrier code contains only alphanumeric characters and underscores
   *
   * @param code - Carrier code to validate
   * @throws Error if code is invalid
   */
  private validateCarrierCode(code: string): void {
    if (!code || code.trim() === '') {
      throw new Error('Carrier code cannot be empty');
    }

    const validPattern = /^[a-zA-Z0-9_]+$/;
    if (!validPattern.test(code)) {
      throw new Error('Carrier code must contain only alphanumeric characters and underscores');
    }
  }

  /**
   * Validates that the method code contains only alphanumeric characters and underscores
   *
   * @param method - Method code to validate
   * @throws Error if method code is invalid
   */
  private validateMethodCode(method: string): void {
    if (!method || method.trim() === '') {
      throw new Error('Method code cannot be empty');
    }

    const validPattern = /^[a-zA-Z0-9_]+$/;
    if (!validPattern.test(method)) {
      throw new Error('Method code must contain only alphanumeric characters and underscores');
    }
  }

  /**
   * Sets the title for the shipping carrier
   *
   * @param title - Display title for the carrier
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * carrier.setTitle('FedEx Express');
   * ```
   */
  setTitle(title: string): this {
    this.carrierData.title = title;
    return this;
  }

  /**
   * Sets the stores for the shipping carrier
   *
   * @param stores - Array of store codes
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * carrier.setStores(['default', 'store1', 'store2']);
   * ```
   */
  setStores(stores: string[]): this {
    this.carrierData.stores = stores;
    return this;
  }

  /**
   * Sets the countries for the shipping carrier
   *
   * @param countries - Array of country codes
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * carrier.setCountries(['US', 'CA', 'MX']);
   * ```
   */
  setCountries(countries: string[]): this {
    this.carrierData.countries = countries;
    return this;
  }

  /**
   * Sets the sort order for the shipping carrier
   *
   * @param sortOrder - Sort order number
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * carrier.setSortOrder(10);
   * ```
   */
  setSortOrder(sortOrder: number): this {
    this.carrierData.sort_order = sortOrder;
    return this;
  }

  /**
   * Sets the active status for the shipping carrier
   *
   * @param active - Active status
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * carrier.setActive(true);
   * carrier.setActive(false);
   * ```
   */
  setActive(active: boolean): this {
    this.carrierData.active = active;
    return this;
  }

  /**
   * Sets the tracking availability for the shipping carrier
   *
   * @param trackingAvailable - Tracking availability status
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * carrier.setTrackingAvailable(true);
   * carrier.setTrackingAvailable(false);
   * ```
   */
  setTrackingAvailable(trackingAvailable: boolean): this {
    this.carrierData.tracking_available = trackingAvailable;
    return this;
  }

  /**
   * Sets the shipping labels availability for the shipping carrier
   *
   * @param shippingLabelsAvailable - Shipping labels availability status
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * carrier.setShippingLabelsAvailable(true);
   * carrier.setShippingLabelsAvailable(false);
   * ```
   */
  setShippingLabelsAvailable(shippingLabelsAvailable: boolean): this {
    this.carrierData.shipping_labels_available = shippingLabelsAvailable;
    return this;
  }

  /**
   * Sets the carrier data from a ShippingCarrierData object
   * Note: The code property cannot be changed once set in the constructor
   *
   * @param carrierData - Carrier data object
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * carrier.setData({
   *   code: 'fedex',
   *   title: 'FedEx Express',
   *   stores: ['default'],
   *   countries: ['US', 'CA'],
   *   sort_order: 10,
   *   active: true,
   *   tracking_available: true,
   *   shipping_labels_available: true
   * });
   * ```
   */
  setData(carrierData: ShippingCarrierData): this {
    // Preserve the original code - it cannot be changed
    const originalCode = this.carrierData.code as string;

    // Validate if a code is provided in the input data (even if empty string)
    if (carrierData.code !== undefined) {
      this.validateCarrierCode(carrierData.code);
    }

    // Copy all data but restore the original code
    this.carrierData = { ...carrierData, code: originalCode };
    return this;
  }

  /**
   * Adds a shipping method to the carrier using a callback pattern
   *
   * @param method - Unique method for the shipping rate
   * @param callback - Optional callback function to configure the method
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * builder.addMethod('standard', (method) => {
   *   method.setMethodTitle('Standard Shipping')
   *       .setPrice(9.99)
   *       .setCost(5.00)
   *       .addAdditionalData('delivery_time', '3-5 business days');
   * });
   * ```
   */
  addMethod(method: string, callback?: (builder: ShippingCarrierMethod) => void): this {
    this.validateMethodCode(method);

    const methodBuilder = new ShippingCarrierMethod(this.carrierData.code as string, method);

    if (callback) {
      callback(methodBuilder);
    }

    this.addedMethods.push(methodBuilder.getData());
    return this;
  }

  /**
   * Removes a shipping method from the carrier
   *
   * @param method - Method code to remove
   * @returns The builder instance for method chaining
   *
   * @example
   * ```typescript
   * builder.removeMethod('express');
   * ```
   */
  removeMethod(method: string): this {
    this.validateMethodCode(method);
    this.removedMethods.push(method);
    return this;
  }

  /**
   * Gets and returns the shipping carrier data as a JSON object
   *
   * @returns The shipping carrier data
   *
   * @example
   * ```typescript
   * const carrierData = carrier.getData();
   * // Returns:
   * // {
   * //   code: 'DPS',
   * //   title: 'Demo Postal Service',
   * //   stores: ['default'],
   * //   countries: ['US', 'CA'],
   * //   sort_order: 10,
   * //   active: true,
   * //   tracking_available: true,
   * //   shipping_labels_available: true
   * // }
   * ```
   */
  getData(): ShippingCarrierData {
    return this.carrierData as ShippingCarrierData;
  }

  /**
   * Gets the list of methods that have been added to the carrier
   *
   * @returns Array of added shipping carrier methods
   *
   * @example
   * ```typescript
   * const addedMethods = carrier.getAddedMethods();
   * // Returns: [{ carrier_code: 'fedex', method: 'standard', ... }, ...]
   * ```
   */
  getAddedMethods(): ShippingCarrierMethodData[] {
    return this.addedMethods;
  }

  /**
   * Gets the list of method codes that have been marked for removal
   *
   * @returns Array of method codes to be removed
   *
   * @example
   * ```typescript
   * const removedMethods = carrier.getRemovedMethods();
   * // Returns: ['overnight', 'express']
   * ```
   */
  getRemovedMethods(): string[] {
    return this.removedMethods;
  }
}

export default ShippingCarrier;
